import {Autocomplete, Box} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import React, {HTMLAttributes, useState} from "react";
import {useTranslation} from "react-i18next";
import {formatDuration} from "../../../../digitalSigning/public/format.util";
import {FolioAutosizeInput, FolioCellBox} from "./FolioCellBox";
import {FolioFormat} from "./folioFormat";
import FolioServiceOption from "./FolioServiceOption";
import {useServicesRates} from "./lineRate";
import {serviceOptionDetail, serviceOptionPriceCents} from "./serviceOption.util";

interface Props {
    services: Service[]
    line: FolioItem
    categories: ServiceCategory[]
    format: FolioFormat
    /** The line's start: the options show each service's price on that day. */
    date?: Date
    error?: boolean
    autoFocus?: boolean
    /** Right after the name, inside the box (the service's info icon). */
    trailing?: React.ReactNode
    onPick: (service: Service) => void
    onBlur: () => void
}

const optionKey = (service: Service) => service.id ?? `name:${service.name}`;
// The options need more room than the service column has
const POPPER_STYLE = {width: 360, maxWidth: 'calc(100vw - 32px)'};

// Clicks and keys on the info icon are its own: they must not open the options or move the input
const stop = (event: React.SyntheticEvent) => event.stopPropagation();

/**
 * "Choose a service" in the cell box: type to search, arrows and Enter to pick. Each option shows the
 * service's duration and category and its price on the line's date. A line whose text matches no
 * service keeps showing that text.
 */
const FolioServicePicker = ({services, line, categories, format, date, error, autoFocus, trailing, onPick, onBlur}: Props) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const prices = useServicesRates(services, date, open);

    const matched = services.find(service => !!line.serviceId && service.id === line.serviceId)
        ?? services.find(service => !!line.description && service.name === line.description);
    const orphan: Service | undefined = !matched && line.description ? {name: line.description} : undefined;
    const value = matched ?? orphan ?? null;
    const labelOf = (option: Service) => option.name || t('No service name');
    // The name is there in the first render - Autocomplete would fill it in an effect, a blank frame
    // whenever a folio's lines appear
    const [inputValue, setInputValue] = useState(() => (value ? labelOf(value) : ''));

    const priceText = (service: Service) => {
        const cents = date ? serviceOptionPriceCents(service, prices.rates, date) : undefined;
        return cents != null ? format.money(cents) : undefined;
    };

    return (
        <Autocomplete<Service, false, false, false>
            fullWidth
            openOnFocus
            forcePopupIcon={false}
            options={orphan ? [orphan, ...services] : services}
            value={value}
            inputValue={inputValue}
            onInputChange={(_event, next) => setInputValue(next)}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionLabel={labelOf}
            isOptionEqualToValue={(option, selected) => optionKey(option) === optionKey(selected)}
            filterOptions={(options, {inputValue}) => {
                const query = inputValue.toLowerCase();
                return options.filter(option => !!option.id && !!option.name?.toLowerCase().includes(query));
            }}
            renderOption={(props, option) => {
                const {key, ...optionProps} = props as HTMLAttributes<HTMLLIElement> & {key?: React.Key};
                return (
                    <FolioServiceOption
                        key={key ?? optionKey(option)}
                        optionProps={optionProps}
                        name={labelOf(option)}
                        detail={serviceOptionDetail(option, categories, minutes => formatDuration(minutes, format.language)) || undefined}
                        price={priceText(option)}
                        priceLoading={prices.loading}
                    />
                );
            }}
            onChange={(_event, service) => {
                if (service?.id) onPick(service);
            }}
            onBlur={onBlur}
            slotProps={{popper: {style: POPPER_STYLE}}}
            renderInput={params => {
                const inputProps = params.inputProps as React.InputHTMLAttributes<HTMLInputElement>;
                return (
                    <FolioCellBox ref={params.InputProps.ref} editable tone={error ? 'error' : 'default'}>
                        <FolioAutosizeInput
                            sizeText={String(inputProps.value || '') || t('Choose a service')}
                            inputProps={{
                                ...params.inputProps,
                                autoFocus,
                                placeholder: t('Choose a service'),
                                title: value?.name ?? '',
                                'aria-label': t('Service'),
                                'aria-invalid': !!error,
                            }}
                        />
                        {trailing && (
                            <Box component={'span'} sx={{display: 'inline-flex', flexShrink: 0}} onMouseDown={stop} onClick={stop} onKeyDown={stop}>
                                {trailing}
                            </Box>
                        )}
                    </FolioCellBox>
                );
            }}
        />
    );
};

export default FolioServicePicker;
